import seedDraws from '../data/seedDraws.json';

const STORAGE_KEY = 'loto649_draws_db_v2';
const LEGACY_STORAGE_KEY = 'loto649_draws_db_v1';
const DRAW_COUNT = 6;
const MIN_NUMBER = 1;
const MAX_NUMBER = 49;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const normalizeText = (value) => String(value ?? '').trim();

const isValidDate = (value) => {
  if (!DATE_PATTERN.test(value)) return false;

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

const normalizeNumbers = (numbers) => {
  if (!Array.isArray(numbers) || numbers.length !== DRAW_COUNT) {
    throw new Error('Câmpul "numbers" trebuie să conțină exact 6 numere.');
  }

  const normalized = numbers.map((number) => {
    const value = typeof number === 'string' ? Number(number.trim()) : number;

    if (!Number.isInteger(value) || value < MIN_NUMBER || value > MAX_NUMBER) {
      throw new Error('Toate numerele trebuie să fie întregi distincte între 1 și 49.');
    }

    return value;
  });

  if (new Set(normalized).size !== DRAW_COUNT) {
    throw new Error('O extragere nu poate conține numere duplicate.');
  }

  return normalized.sort((first, second) => first - second);
};

const createDrawKey = (draw) => {
  if (draw.drawId) return `id:${draw.drawId.toLowerCase()}`;
  return `date:${draw.date}`;
};

const areDrawsEqual = (first, second) => (
  first.drawId === second.drawId &&
  first.date === second.date &&
  first.drawType === second.drawType &&
  first.numbers.join(',') === second.numbers.join(',')
);

const normalizeDraw = (item, index) => {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    throw new Error(`Înregistrarea ${index + 1} nu este un obiect JSON valid.`);
  }

  const drawId = normalizeText(item.drawId);
  const date = normalizeText(item.date);
  const drawType = normalizeText(item.drawType) || 'Extragere standard';

  if (!drawId && !date) {
    throw new Error(`Înregistrarea ${index + 1} necesită "drawId" sau "date".`);
  }

  if (!date || !isValidDate(date)) {
    throw new Error(
      `Înregistrarea ${index + 1} are o dată invalidă. Folosește formatul YYYY-MM-DD.`
    );
  }

  return {
    drawId,
    date,
    numbers: normalizeNumbers(item.numbers),
    drawType
  };
};

const readArray = (storageKey) => {
  const raw = localStorage.getItem(storageKey);
  if (!raw) return null;

  const parsed = JSON.parse(raw);
  return Array.isArray(parsed) ? parsed : null;
};

const normalizeStoredDraws = (draws) => {
  const normalized = [];

  draws.forEach((draw, index) => {
    try {
      normalized.push(normalizeDraw(draw, index));
    } catch {
      // Înregistrările locale corupte nu sunt propagate în baza validată.
    }
  });

  return normalized;
};

export const getStoredDraws = () => {
  try {
    const current = readArray(STORAGE_KEY);

    if (current) {
      return normalizeStoredDraws(current);
    }

    const legacy = readArray(LEGACY_STORAGE_KEY);
    const initial = normalizeStoredDraws(legacy || seedDraws);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
    return initial;
  } catch {
    return normalizeStoredDraws(seedDraws);
  }
};

export const validateDrawImport = (incomingArray) => {
  if (!Array.isArray(incomingArray)) {
    throw new Error('Fișierul JSON trebuie să conțină o listă de extrageri.');
  }

  if (!incomingArray.length) {
    throw new Error('Fișierul JSON nu conține nicio extragere.');
  }

  const validDraws = [];
  const errors = [];
  const seenKeys = new Set();
  const seenDates = new Set();

  incomingArray.forEach((item, index) => {
    try {
      const draw = normalizeDraw(item, index);
      const key = createDrawKey(draw);

      if (seenKeys.has(key)) {
        throw new Error(`Înregistrarea ${index + 1} este duplicată în fișier.`);
      }

      if (seenDates.has(draw.date)) {
        throw new Error(
          `Înregistrarea ${index + 1} dublează data ${draw.date} în același fișier.`
        );
      }

      seenKeys.add(key);
      seenDates.add(draw.date);
      validDraws.push(draw);
    } catch (error) {
      errors.push(error.message);
    }
  });

  return {
    validDraws,
    errors,
    receivedCount: incomingArray.length
  };
};

export const mergeDrawsDelta = (incomingArray) => {
  const validation = validateDrawImport(incomingArray);

  if (validation.errors.length) {
    throw new Error(
      `Importul a fost anulat. ${validation.errors.slice(0, 5).join(' ')}`
    );
  }

  const currentDraws = getStoredDraws();
  const existingByKey = new Map();
  const keyByDate = new Map();

  currentDraws.forEach((draw) => {
    const key = createDrawKey(draw);
    existingByKey.set(key, draw);
    keyByDate.set(draw.date, key);
  });

  let addedCount = 0;
  let updatedCount = 0;
  let skippedCount = 0;

  validation.validDraws.forEach((draw) => {
    const key = createDrawKey(draw);
    const matchingKey = existingByKey.has(key) ? key : keyByDate.get(draw.date);
    const existing = matchingKey ? existingByKey.get(matchingKey) : null;

    if (!existing) {
      existingByKey.set(key, draw);
      keyByDate.set(draw.date, key);
      addedCount += 1;
      return;
    }

    if (areDrawsEqual(existing, draw)) {
      skippedCount += 1;
      return;
    }

    existingByKey.delete(matchingKey);
    existingByKey.set(key, draw);
    keyByDate.set(draw.date, key);
    updatedCount += 1;
  });

  const merged = Array.from(existingByKey.values()).sort(
    (first, second) => second.date.localeCompare(first.date)
  );

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    throw new Error(
      'Browserul nu a putut salva datele. Spațiul local poate fi plin sau blocat.'
    );
  }

  return {
    total: merged.length,
    receivedCount: validation.receivedCount,
    addedCount,
    updatedCount,
    skippedCount
  };
};

export const exportStoredDraws = () => (
  JSON.stringify(getStoredDraws(), null, 2)
);

export const getDemoJsonStructure = () => (
  JSON.stringify(
    [
      {
        drawId: 'RO-6-49-2025-001',
        date: '2025-01-05',
        numbers: [4, 12, 19, 28, 37, 45],
        drawType: 'Duminică'
      },
      {
        drawId: 'RO-6-49-2025-002',
        date: '2025-01-09',
        numbers: [7, 15, 22, 31, 40, 48],
        drawType: 'Joi'
      }
    ],
    null,
    2
  )
);